import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setAdditionalAction,
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import { getDB } from "features/medical-scribe/medical-scribe-db/medical-scribe-db.util";
import { ambientScribeApi } from "services/ambient-scribe";

import {
  buildLocalRecordingFile,
  findLocalRecordingWithAudio
} from "./local-recording.util";

export const AUDIO_EXPORT_POLL_INTERVAL_MS = 3000;
/** About two minutes of polling: long enough for a two-hour visit to convert. */
export const AUDIO_EXPORT_MAX_POLLS = 40;

export const SERVER_AUDIO_COPY = {
  noAudio: "This scribe has no audio to download.",
  originalDownloaded:
    "Downloaded the original recording. If it won’t play, open it in Google Chrome.",
  failed: "Couldn’t download the audio. Try again.",
  noLocalCopy: "There is no audio saved on this computer for this scribe."
} as const;

export type ServerAudioDownloadPhase = "idle" | "preparing" | "downloading";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** The recording's own extension when its URL has one, never its S3 file name. */
function extensionOf(url: string): string {
  try {
    const match = /\.([a-z0-9]{2,5})$/i.exec(new URL(url).pathname);
    return match ? match[1].toLowerCase() : "webm";
  } catch {
    return "webm";
  }
}

/**
 * Downloads a scribe's audio for the split button (SN-19937, AMBIENT_SCRIBE_SERVER_AUDIO_DOWNLOAD).
 *
 * The main action asks the server for an MP3 of the whole recording and polls while it converts.
 * Anything short of a saved MP3 (a failed or slow conversion, a 404, a network error) falls back to
 * the original WebM, read through a freshly signed URL because the one the dialog opened with
 * expires after an hour. Only a file whose request succeeded is saved: an S3 error page saved as
 * `.webm` is what the old button did.
 *
 * A download stops quietly when the dialog closes or switches to another scribe.
 */
export function useScribeServerAudioDownload({
  remoteMedicalScribeId,
  localId
}: {
  remoteMedicalScribeId: number | undefined;
  localId: string | undefined;
}) {
  const dispatch = useAppDispatch();
  const runRef = useRef(0);
  // The phase belongs to one run for one scribe. Switching scribes mid-download shows idle at once,
  // and a cancelled run still settles its own phase when it ends.
  const scribeKey = `${remoteMedicalScribeId ?? ""}:${localId ?? ""}`;
  const [progress, setProgress] = useState<{
    run: number;
    key: string;
    phase: ServerAudioDownloadPhase;
  }>({ run: 0, key: scribeKey, phase: "idle" });
  const phase = progress.key === scribeKey ? progress.phase : "idle";

  useEffect(
    () => () => {
      runRef.current += 1;
    },
    [remoteMedicalScribeId, localId]
  );

  const beginRun = useCallback(
    (initialPhase: ServerAudioDownloadPhase) => {
      const run = (runRef.current += 1);
      const setPhase = (next: ServerAudioDownloadPhase) =>
        setProgress({ run, key: scribeKey, phase: next });
      setPhase(initialPhase);
      return {
        isCancelled: () => runRef.current !== run,
        setPhase,
        finish: () =>
          setProgress((prev) =>
            prev.run === run ? { ...prev, phase: "idle" } : prev
          )
      };
    },
    [scribeKey]
  );

  const notify = useCallback(
    (message: string) => {
      dispatch(setAdditionalAction(undefined));
      dispatch(setCustomMessage(message));
      dispatch(setSnackbarOpen(true));
    },
    [dispatch]
  );

  /** Saves the file at `url`. False when the request failed; nothing is saved then. */
  const saveFromUrl = useCallback(
    async (url: string, fileName: string, isCancelled: () => boolean) => {
      try {
        const response = await fetch(url);
        if (!response.ok) return false;
        const blob = await response.blob();
        if (!isCancelled()) saveBlob(blob, fileName);
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  /** The signed MP3 URL once the server has one, or `null` to fall back to the WebM. */
  const waitForMp3 = useCallback(
    async (id: number, isCancelled: () => boolean) => {
      for (let poll = 0; poll < AUDIO_EXPORT_MAX_POLLS; poll += 1) {
        if (poll > 0) await sleep(AUDIO_EXPORT_POLL_INTERVAL_MS);
        if (isCancelled()) return null;
        try {
          const result = await dispatch(
            ambientScribeApi.endpoints.getScribeAudioExport.initiate(
              { id },
              { subscribe: false, forceRefetch: true }
            )
          ).unwrap();
          if (result.status === "success") return result.recording;
          if (result.status !== "processing") return null;
        } catch {
          return null;
        }
      }
      return null;
    },
    [dispatch]
  );

  const downloadOriginal = useCallback(
    async (
      id: number,
      isCancelled: () => boolean,
      setPhase: (next: ServerAudioDownloadPhase) => void
    ) => {
      let recording: string | null | undefined;
      try {
        const fresh = await dispatch(
          ambientScribeApi.endpoints.getAmbientScribe.initiate(
            { id, query: "{id,recording}" },
            { subscribe: false, forceRefetch: true }
          )
        ).unwrap();
        recording = fresh.recording ?? null;
      } catch {
        if (!isCancelled()) notify(SERVER_AUDIO_COPY.failed);
        return;
      }
      if (isCancelled()) return;
      if (!recording) {
        notify(SERVER_AUDIO_COPY.noAudio);
        return;
      }
      setPhase("downloading");
      const saved = await saveFromUrl(
        recording,
        `scribe-recording-${id}.${extensionOf(recording)}`,
        isCancelled
      );
      if (isCancelled()) return;
      notify(
        saved ? SERVER_AUDIO_COPY.originalDownloaded : SERVER_AUDIO_COPY.failed
      );
    },
    [dispatch, notify, saveFromUrl]
  );

  const downloadServerAudio = useCallback(async () => {
    if (!remoteMedicalScribeId) return;
    const { isCancelled, setPhase, finish } = beginRun("preparing");
    try {
      const mp3Url = await waitForMp3(remoteMedicalScribeId, isCancelled);
      if (isCancelled()) return;
      if (mp3Url) {
        setPhase("downloading");
        const saved = await saveFromUrl(
          mp3Url,
          `scribe-recording-${remoteMedicalScribeId}.mp3`,
          isCancelled
        );
        if (saved || isCancelled()) return;
      }
      await downloadOriginal(remoteMedicalScribeId, isCancelled, setPhase);
    } finally {
      finish();
    }
  }, [
    remoteMedicalScribeId,
    beginRun,
    waitForMp3,
    saveFromUrl,
    downloadOriginal
  ]);

  const downloadLocalAudio = useCallback(async () => {
    const { isCancelled, finish } = beginRun("downloading");
    try {
      const db = await getDB();
      const recording = await findLocalRecordingWithAudio(
        db,
        remoteMedicalScribeId,
        localId
      );
      if (isCancelled()) return;
      if (!recording) {
        notify(SERVER_AUDIO_COPY.noLocalCopy);
        return;
      }
      const { blob, extension } = await buildLocalRecordingFile(recording);
      if (isCancelled()) return;
      saveBlob(
        blob,
        `scribe-recording-${remoteMedicalScribeId || recording.recordingId}.${extension}`
      );
    } catch {
      if (!isCancelled()) notify(SERVER_AUDIO_COPY.failed);
    } finally {
      finish();
    }
  }, [remoteMedicalScribeId, localId, beginRun, notify]);

  return { downloadServerAudio, downloadLocalAudio, phase };
}
